import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import * as Promise from 'bluebird';
import { Component } from 'react';
import {
  ColumnDefinition,
  getFilters,
  getOrder,
  getSearchFilter,
  getSearchOrder,
  getServerFieldSectionWhere,
  getServerQueryFilter,
  isFilterExist,
  isSortExist,
  TABLE_SORT_DIRECTION,
} from 'Src/helpers/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { parse } from 'query-string';
import { Loader } from 'Src/components/Loader/Loader';
import { Grid } from 'Src/components/Grid/Grid';
import { History, Location } from 'history';
import {
  getNotifications,
  getNotificationsCount,
  getAllNotifications,
} from '../../../../../../services/superadmin/notifications';
import {
  getNotificationChannelType,
  getNotificationStatus,
  getCreatedAtWithTime,
  getNotificationActionDescriptorType,
} from '../../../../../../helpers/accessor';
import { ResizablePanel } from '../../../../../../components/ResizablePanel/ResizablePanel';
import { GenericSummary } from '../../../../../../components/GenericSummary/GenericSummary';
import { NOTIFICATION_TABS } from '../../../../../../consts/notifications';
import { TABS } from '../../../../../../consts/common';
import { CustomerNotificationSummary } from '../Customers/CustomerNotificationSummary/CustomerNotificationSummary';
import {
  getSelectOptionForChannelTypeNotification,
  getSelectOptionForStatusNotification,
} from '../../../../../../helpers/select';
import { convertToFilterWithPlus } from '../../../../../../helpers/table';
import * as propz from 'propz';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Email',
    field: 'channel.email',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: item => propz.get(item, ['channel', 'email'], ''),
  },
  {
    text: 'Action descriptor type',
    field: 'actionDescriptorType',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getNotificationActionDescriptorType,
  },
  {
    text: 'Channel type',
    field: 'channelType',
    isSort: false,
    type: FILTER_TYPE.SELECT,
    accessor: getNotificationChannelType,
  },
  {
    text: 'Status',
    field: 'statusNotification',
    isSort: false,
    type: FILTER_TYPE.SELECT,
    accessor: getNotificationStatus,
  },
  {
    text: 'Created at',
    field: 'createdAt',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getCreatedAtWithTime,
  },
];

interface State {
  items: any[];
  currentPage: number;
  selectedItems: any[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  currentTabIndex: number;
  total: number;
  basePath: string;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class Notifications extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      currentTabIndex: 0,
      total: 0,
      basePath: '',
    };
  }

  componentDidMount() {
    this.setState({ isLoading: true });
    this.setItems();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({ isLoading: true });
      this.setItems();
    }
  }

  setItems() {
    const { history, user } = this.props;

    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters(COLUMNS, search);
    const isShowFilter = isFilterExist(filters);
    const isDataFiltered = isFilterExist(filters);
    const where = getServerFieldSectionWhere(filters);
    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter(page, where, order);

    const getItemsPromise = getNotifications(user, serverQueryFilter);
    const getItemsCountPromise = getNotificationsCount(user, where);

    return Promise.all([getItemsCountPromise, getItemsPromise]).then(([countObj, items]) => {
      this.setState({
        currentPage: page,
        sortDirection: sortDirection as TABLE_SORT_DIRECTION,
        sortColumnsName: sortColumnsName,
        isShowFilter: isShowFilter,
        isDataFiltered: isDataFiltered,
        filters: filters,
        items: items,
        total: countObj.count,
        basePath: history.location.pathname,
        isLoading: false,
      });

      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search = [];
    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&'),
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];
    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({ selectedItems: selectedItemsUpdated });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;
    const itemsFiltered = items.filter(item => selectedItems.every(selectedItem => selectedItem.id !== item.id));
    this.setState({
      selectedItems: [...selectedItems, ...itemsFiltered],
      isSelectAllChecked: true,
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;
    const { user } = this.props;

    this.setState({ isLoading: true });

    const where = getServerFieldSectionWhere(filters);

    getAllNotifications(user, where).then(items => {
      const itemsFiltered = items.filter(item => selectedItems.every(selectedItem => selectedItem.id !== item.id));
      this.setState({
        selectedItems: [...selectedItems, ...itemsFiltered],
        isSelectAllChecked: true,
        isLoading: false,
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({ selectedItems: [], isSelectAllChecked: false });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;
    const order = getOrder(sortField, sortDirection, sortColumnsName);
    let search = [`order=${sortField}:${order}`];

    if (isFilterExist(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&'),
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];
    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = { ...nextFilters, [filterField]: { ...currentFilterField, from: filterValue } };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = { ...nextFilters, [filterField]: { ...currentFilterField, to: filterValue } };
          break;
      }
    } else {
      nextFilters = { ...nextFilters, [filterField]: filterValue };
    }

    this.setState({ filters: nextFilters });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const filtersConverted = convertToFilterWithPlus(filters);

    if (isFilterExist(filters)) {
      search.push(getSearchFilter(filtersConverted));
    }

    this.setState({ selectedItems: [], isSelectAllChecked: false });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&'),
    });
  };

  onClearFilterClick = (): void => {
    this.setState({ selectedItems: [], isSelectAllChecked: false });
    this.props.history.push({ pathname: this.state.basePath, search: '' });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();
    this.setState({ isShowFilter: !this.state.isShowFilter });
  };

  onTabClick = (event, index: number) => {
    event.preventDefault();
    this.setState({ currentTabIndex: index });
  };

  getTabs(): React.ReactNode {
    const { currentTabIndex, selectedItems } = this.state;
    const currentTab = NOTIFICATION_TABS[currentTabIndex];

    switch (currentTab) {
      case TABS.SUMMARY:
        return <CustomerNotificationSummary notificationItem={selectedItems[0]} />;
      default:
        return <div />;
    }
  }

  renderSummary(): React.ReactNode {
    const { selectedItems, currentTabIndex } = this.state;

    if (selectedItems.length !== 1) {
      return <div className="font-weight-bold">Select a notification above</div>;
    }

    return (
      <GenericSummary onTabClick={this.onTabClick} tabs={NOTIFICATION_TABS} currentTabIndex={currentTabIndex}>
        {this.getTabs()}
      </GenericSummary>
    );
  }

  renderGrid(): React.ReactNode {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      isDataFiltered,
      selectedItems,
      filters,
      isSelectAllChecked,
      currentPage,
      total,
    } = this.state;

    const filterOptions = {
      statusNotification: getSelectOptionForStatusNotification(),
      channelType: getSelectOptionForChannelTypeNotification(),
    };

    return (
      <Grid
        dataItems={items}
        filters={filters}
        currentPage={currentPage}
        total={total}
        isSelectAllChecked={isSelectAllChecked}
        isDataFiltered={isDataFiltered}
        sortDirection={sortDirection}
        sortColumnsName={sortColumnsName}
        isShowFilter={isShowFilter}
        dataItemsSelected={selectedItems}
        columns={COLUMNS}
        actionItems={[]}
        options={filterOptions}
        onItemClick={this.onItemClick}
        onSortClick={this.onTableSortClick}
        onApplyFilterClick={this.onApplyFilterClick}
        onClearFilterClick={this.onClearFilterClick}
        onTableFilterChange={this.onTableFilterChange}
        onTableFilterClick={this.onTableFilterClick}
        setCurrentPageParams={this.setCurrentPageParams}
        onSelectAllClick={this.onSelectAllClick}
        onSelectAllOnPageClick={this.onSelectAllOnPageClick}
        onUnselectAllClick={this.onUnselectAllClick}
      />
    );
  }

  render() {
    if (this.state.isLoading) {
      return <Loader />;
    }

    return (
      <div className="mt-3">
        <div className="row">
          <div className="col-md-12">
            <ResizablePanel>
              {this.renderGrid()}
              {this.renderSummary()}
            </ResizablePanel>
          </div>
        </div>
      </div>
    );
  }
}
